import getHistory from 'globalHistory';
import moment from 'moment';
import _ from 'lodash';

import { format } from '@breezeway/brzw-react-core';
import { helpers as calendarHelpers } from '@common/brzw_calendar';
import {
  getTaskHomeId,
  shouldTaskAssignmentDisplay,
  shouldTaskAssignmentStatusDisplay,
  shouldTaskStatusDisplay,
} from '@common/utils/task';
import { getParentGroup } from '@common/utils/company';
import { categorizeReservations } from '@common/utils/reservations';
import {
  PAGINATION_SETTINGS,
  SCHEDULE_ALL_DAY_CELL_KEY,
  SCHEDULE_DATE_MODIFIER,
  SCHEDULE_DAILY_TIME_RANGE,
  SCHEDULE_LOCATIONS,
  SCHEDULE_MODE,
  SCHEDULE_ROUTE_BASE,
  SCHEDULE_ROUTE_DATE_FORMAT,
  SCHEDULE_ROUTE_MONTHLY_DATE_FORMAT,
  SCHEDULE_UNASSIGNED_ID,
} from './constants';

const { formatDate } = format.datetime;

/**
 * @param {Moment} activeDate
 * @return {Moment}
 */
export const getActiveDateOnChange = activeDate => {
  const todayMoment = moment();

  if (activeDate.isSame(todayMoment, 'week')) {
    return todayMoment;
  }

  return calendarHelpers.getWeekBegin(activeDate);
};

/**
 * @param {Moment|Date\String} activeDate
 * @return {String}
 */
export const convertToMonthly = activeDate => {
  const clonedDate = calendarHelpers.cloneDate(activeDate);

  return formatDate(clonedDate, false, SCHEDULE_ROUTE_MONTHLY_DATE_FORMAT);
};

/**
 * @param {Moment|Date\String} activeDate
 * @return {moment}
 */
export const setActiveDateTime = (activeDate, hour) => {
  const clonedDate = calendarHelpers.cloneDate(activeDate);

  return clonedDate.hour(hour).startOf('hour');
};

/**
 * @description Convert col1-23-05-2018 to 23-05-2018
 * @param {String}
 * @return {String}
 */
export const getDateFromTimeKey = timeKey => timeKey.replace(/col.-/g, '');

/**
 * @param {moment|Date\String} activeDate
 * @param {String} mode
 * @param {String} subject
 */
export const updateScheduleRoute = (activeDate, mode, subject) => {
  const clonedDate = calendarHelpers.cloneDate(activeDate);
  const dateFormatPattern =
    mode === SCHEDULE_MODE.MONTHLY
      ? SCHEDULE_ROUTE_MONTHLY_DATE_FORMAT
      : SCHEDULE_ROUTE_DATE_FORMAT;
  let formattedDate = formatDate(clonedDate, false, dateFormatPattern);

  // Fallback for invalid formattedDate - edge case
  if (!_.isString(formattedDate)) {
    formattedDate = formatDate(moment(), false, dateFormatPattern);
  }

  const path = ['', SCHEDULE_ROUTE_BASE, subject, formattedDate, mode].join('/');
  getHistory().push(path);
};

/**
 * @param {Array.<Object>} tasks
 * @param {Array.<Object>} filters
 * @return {Array.<Object>}
 */
export const applyScheduleFilters = (tasks, filters, companyGroups) => {
  const {
    assignments: assignmentsFilters,
    assignmentStatus: assignmentStatusFilters,
    departments: departmentsFilters,
    groups: groupsFilters,
    statuses: statusesFilters,
  } = filters;

  const filterByAssignments = _.size(assignmentsFilters) > 0;
  const filterByDepartments = _.size(departmentsFilters) > 0;
  const filterByStatuses = _.size(statusesFilters) > 0;
  const filterByGroups = _.size(groupsFilters) > 0;
  const filterByAssignmentStatus = _.size(assignmentStatusFilters) > 0;

  return _.filter(tasks, task => {
    const taskGroupId = _.get(task, 'home.company_group_id', '');
    const parentGroupId = _.get(getParentGroup(companyGroups, taskGroupId), 'id', null);

    if (filterByDepartments && !_.includes(departmentsFilters, 'hasTask')) {
      const taskDepartmentCode = _.get(task, 'type_department.code', '');

      if (!_.includes(departmentsFilters, taskDepartmentCode)) {
        return false;
      }
    }

    if (filterByStatuses && !shouldTaskStatusDisplay(task, statusesFilters)) {
      return false;
    }

    if (filterByAssignments && !shouldTaskAssignmentDisplay(task, assignmentsFilters)) {
      return false;
    }

    if (
      filterByAssignmentStatus &&
      !shouldTaskAssignmentStatusDisplay(task, assignmentStatusFilters)
    ) {
      return false;
    }

    if (
      filterByGroups &&
      !_.includes(groupsFilters, taskGroupId) &&
      !_.includes(groupsFilters, parentGroupId)
    ) {
      return false;
    }

    return true;
  });
};

/**
 * @param {Array.<Object>}
 * @param {Array.<Object>}
 * @return {Array.<Object>}
 */
export const countTasks = propertiesRelated => {
  const propertiesRelatedValues = _.values(propertiesRelated);
  let taskCount = 0;

  _.forEach(propertiesRelatedValues, values => {
    if (_.isArray(values)) {
      taskCount += _.size(values);
    } else {
      taskCount += _.size(values.tasks);
    }
  });

  return taskCount;
};

/**
 * @param {Object}
 * @return {Array.<Number>}
 */
export const getTasksIdsFromRelated = subjectsRelated => {
  const tasksIds = [];

  _.forEach(subjectsRelated, subjectRelated => {
    const { tasks } = subjectRelated;

    _.forEach(tasks, task => {
      tasksIds.push(task.id);
    });
  });

  return tasksIds;
};

/**
 * @param {String} mode
 * @param {Object} callbacks
 */
export const modeGate = (mode, callbacks = {}) => {
  switch (mode) {
    case SCHEDULE_MODE.DAILY:
    default: {
      if (_.isFunction(callbacks[SCHEDULE_MODE.DAILY])) {
        callbacks[SCHEDULE_MODE.DAILY]();
      }

      break;
    }
    case SCHEDULE_MODE.WEEKLY: {
      if (_.isFunction(callbacks[SCHEDULE_MODE.WEEKLY])) {
        callbacks[SCHEDULE_MODE.WEEKLY]();
      }

      break;
    }
    case SCHEDULE_MODE.MOBILE: {
      if (_.isFunction(callbacks[SCHEDULE_MODE.MOBILE])) {
        callbacks[SCHEDULE_MODE.MOBILE]();
      }

      break;
    }
  }
};

/**
 * @param {String} mode
 * @param {Moment} activeDate
 * @param {String} timeKey
 * @return {Boolean}
 */
export const isCellInPast = (mode, activeDate, timeKey) => {
  const currentDateMoment = moment();
  let cellInPast = false;

  modeGate(mode, {
    // Check cell time status for daily mode
    [SCHEDULE_MODE.DAILY]: () => {
      if (timeKey !== SCHEDULE_ALL_DAY_CELL_KEY) {
        const timeRange = _.get(SCHEDULE_DAILY_TIME_RANGE, timeKey);
        const timeBorderMoment = setActiveDateTime(activeDate, timeRange.to);

        cellInPast = timeBorderMoment.isBefore(currentDateMoment, 'minutes');
      } else {
        cellInPast = activeDate.isBefore(currentDateMoment, 'day');
      }
    },
    // Check cell time status for weekly mode
    [SCHEDULE_MODE.WEEKLY]: () => {
      // Remove col{colNumber}- prefix from timeKey to get the date
      const cellDate = timeKey.replace(/col.-/g, '');

      cellInPast = currentDateMoment.isAfter(cellDate, 'day');
    },
  });

  return cellInPast;
};

/**
 * @param {String} mode
 * @param {Moment} activeDate
 * @param {String} timeKey
 * @return {}
 */
export const getCellDate = (mode, activeDate, timeKey) => {
  let cellDate = null;

  modeGate(mode, {
    // Check cell time status for daily mode
    [SCHEDULE_MODE.DAILY]: () => {
      if (timeKey !== SCHEDULE_ALL_DAY_CELL_KEY) {
        const timeRange = _.get(SCHEDULE_DAILY_TIME_RANGE, timeKey);

        cellDate = setActiveDateTime(activeDate, timeRange.from);
      } else {
        cellDate = moment(
          activeDate.format(SCHEDULE_ROUTE_DATE_FORMAT),
          SCHEDULE_ROUTE_DATE_FORMAT,
        );
      }
    },
    // Check cell time status for weekly mode
    [SCHEDULE_MODE.WEEKLY]: () => {
      cellDate = moment(getDateFromTimeKey(timeKey), SCHEDULE_ROUTE_DATE_FORMAT);
    },
  });

  return cellDate;
};

/**
 * @param {String} pathname
 * @return {String}
 */
export const getScheduleLocation = (pathname = '') => {
  if (pathname) {
    if (pathname.match(`/${SCHEDULE_LOCATIONS.PROPERTY}`)) {
      return SCHEDULE_LOCATIONS.PROPERTY;
    } else if (pathname.match(`/${SCHEDULE_LOCATIONS.PROPERTY_TASKS}`)) {
      return SCHEDULE_LOCATIONS.PROPERTY_TASKS;
    } else if (pathname.match(`/${SCHEDULE_LOCATIONS.USER_PLANNING}`)) {
      return SCHEDULE_LOCATIONS.USER_PLANNING;
    } else if (pathname.match(`/${SCHEDULE_LOCATIONS.USER}`)) {
      return SCHEDULE_LOCATIONS.USER;
    }
  }

  return SCHEDULE_LOCATIONS.PROPERTY;
};

/**
 * @param {String} pathname
 * @return {Boolean}
 */
export const isPropertyScheduleLocation = pathname =>
  getScheduleLocation(pathname) === SCHEDULE_LOCATIONS.PROPERTY;

/**
 * @param {String} pathname
 * @return {Boolean}
 */
export const isPersonScheduleLocation = pathname =>
  getScheduleLocation(pathname) === SCHEDULE_LOCATIONS.USER;

/**
 * @param {String} pathname
 * @return {Boolean}
 */
export const isPropertyTasksLocation = pathname =>
  getScheduleLocation(pathname) === SCHEDULE_LOCATIONS.PROPERTY_TASKS;

/**
 * @param {String} pathname
 * @return {Boolean}
 */
export const isPersonPlanningScheduleLocation = pathname =>
  getScheduleLocation(pathname) === SCHEDULE_LOCATIONS.USER_PLANNING;

/**
 * @param {String} type
 */
export const getCategorizedReservations = (activeDate, propertiesReservations, task) => {
  const propertyId = getTaskHomeId(task);
  const propertyReservations = _.get(propertiesReservations, propertyId, []);

  if (_.isEmpty(propertyReservations)) {
    return null;
  }

  return categorizeReservations(activeDate, propertyReservations);
};

/**
 * @param {Object} dragPerson
 * @param {Object} dropPerson
 * @param {Object} task
 * @return {Object}
 */
export const onDropReassign = (dragPerson, dropPerson, task) => {
  let assignedPeopleIds = _.map(_.get(task, 'people', []), assignedPerson =>
    _.get(assignedPerson, 'company_people_id', null),
  );

  const dragPersonId = _.get(dragPerson, 'id', null);
  const dropPersonId = _.get(dropPerson, 'id', null);
  const toUnassigned = dropPersonId === SCHEDULE_UNASSIGNED_ID;
  const hasMultiplePeopleAssigned = _.size(assignedPeopleIds) > 1;

  if (toUnassigned && !hasMultiplePeopleAssigned) {
    assignedPeopleIds = [];
  } else if (toUnassigned && hasMultiplePeopleAssigned) {
    assignedPeopleIds = _.filter(
      assignedPeopleIds,
      assignedPersonId => assignedPersonId !== dragPersonId,
    );
  } else {
    assignedPeopleIds = _.without(assignedPeopleIds, dragPersonId);
    assignedPeopleIds = _.uniq([...assignedPeopleIds, dropPersonId]);
  }

  return {
    params: {
      companyPeople: assignedPeopleIds,
      dragPersonId,
      dropPersonId,
    },
    task,
  };
};

/**
 * @param {String} timeString
 * @return {String}
 */
export const prepareDurationTime = timeString => {
  const splittedTimeString = _.split(timeString, ':', 2);
  return _.join(splittedTimeString, ':');
};

/**
 * @param {String} prevMode
 * @param {String} nextMode
 * @param {String} currentActiveDate
 * @return {String}
 */

export const getNewActiveDate = (prevMode, nextMode, currentActiveDate) => {
  const todayMoment = moment();
  const startOfThisWeek = calendarHelpers.getWeekBegin(todayMoment);
  const wasDaily = prevMode === SCHEDULE_MODE.DAILY;
  const wasMonthly = prevMode === SCHEDULE_MODE.MONTHLY;
  const wasWeekly = prevMode === SCHEDULE_MODE.WEEKLY;
  const wasMobile = prevMode === SCHEDULE_MODE.MOBILE;

  let newActiveDate = currentActiveDate;

  const changeToWeekly = nextMode === SCHEDULE_MODE.WEEKLY;
  const changeToDaily = nextMode === SCHEDULE_MODE.DAILY;
  const changeToMobile = nextMode === SCHEDULE_MODE.MOBILE;
  const todayWasInGivenMonth = wasMonthly && todayMoment.isSame(currentActiveDate, 'month');
  const todayWasInGivenWeek = wasWeekly && startOfThisWeek.isSame(currentActiveDate);

  if (changeToWeekly) {
    if (todayWasInGivenMonth) {
      newActiveDate = startOfThisWeek;
    } else if (!wasDaily) {
      newActiveDate = calendarHelpers.getWeekBegin(currentActiveDate);
    }
  }

  if (changeToDaily && (todayWasInGivenWeek || todayWasInGivenMonth)) {
    newActiveDate = todayMoment;
  }

  if (changeToMobile && !wasMobile) {
    newActiveDate = todayMoment;
  }

  return newActiveDate;
};

export const countPaginationFromTo = (page, perPage, totalRecords) => {
  const fromIndex = page * perPage + PAGINATION_SETTINGS.START_POINT;
  let toIndex = (page + PAGINATION_SETTINGS.START_POINT) * perPage;

  if (totalRecords < toIndex) {
    toIndex += totalRecords - toIndex;
  }

  return {
    fromIndex,
    toIndex,
  };
};

/**
 * @param {Moment} activeDate
 * @param {String} mode
 * @return {Object}
 */
export const getActiveDateRange = (activeDate, mode) => {
  const dates = {
    endDate: null,
    startDate: null,
  };

  const formattedStartDate = formatDate(activeDate, null, SCHEDULE_ROUTE_DATE_FORMAT);

  modeGate(mode, {
    [SCHEDULE_MODE.DAILY]: () => {
      dates.endDate = formattedStartDate;
      dates.startDate = formattedStartDate;
    },
    [SCHEDULE_MODE.MOBILE]: () => {
      dates.endDate = formattedStartDate;
      dates.startDate = formattedStartDate;
    },
    [SCHEDULE_MODE.WEEKLY]: () => {
      const endDate = calendarHelpers.modifyActiveDate(
        activeDate,
        SCHEDULE_DATE_MODIFIER.FULL_WEEK,
      );
      const formattedEndDate = formatDate(endDate, null, SCHEDULE_ROUTE_DATE_FORMAT);

      dates.endDate = formattedEndDate;
      dates.startDate = formattedStartDate;
    },
  });

  return dates;
};

/**
 * @param {Object} schedules
 * @return {Object}
 */
export const getSortedSchedulesByDate = schedules => {
  const result = {};

  _.forEach(_.sortBy(_.keys(schedules), (a, b) => moment(b) + moment(a)), key => {
    result[key] = schedules[key];
  });

  return result;
};
